/** Manual stroke order for one image element: reorder the outline's stroke groups. */

import type { RevealPlan } from "@studio/engine";
import { ArrowDown, ArrowUp, ChevronsUp, RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fileUrl } from "../api";
import { useEditor } from "./store";

export function StrokeOrderDialog({ elementId }: { elementId: string }) {
  const project = useEditor((s) => s.project)!;
  const el = project.elements.find((e) => e.id === elementId);
  const assetId = el?.image?.assetId ?? "";
  const stamp = useEditor((s) => s.analysis[assetId]?.stamp);
  const [plan, setPlan] = useState<RevealPlan | null>(null);
  const [layer, setLayer] = useState<HTMLImageElement | null>(null);
  const [order, setOrder] = useState<string[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const close = () => useEditor.getState().set({ panel: null });

  useEffect(() => {
    if (!assetId) return;
    fetch(fileUrl(project.id, `derived/${assetId}/plan.json`, stamp))
      .then((r) => r.json())
      .then((p: RevealPlan) => {
        setPlan(p);
        const ids = p.outline.groups.map((g) => g.id);
        const saved = (el?.image?.groupOrder ?? []).filter((id) => ids.includes(id));
        setOrder([...saved, ...ids.filter((id) => !saved.includes(id))]);
        const img = new Image();
        img.onload = () => setLayer(img);
        img.src = fileUrl(project.id, `derived/${assetId}/${p.layers.outline}`, stamp);
      });
  }, [assetId, project.id, stamp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cv = canvas.current;
    if (!cv || !plan) return;
    const [w, h] = plan.size;
    const k = Math.min(560 / w, 560 / h);
    cv.width = Math.round(w * k);
    cv.height = Math.round(h * k);
    const ctx = cv.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cv.width, cv.height);
    if (layer) {
      ctx.globalAlpha = 0.18;
      ctx.drawImage(layer, 0, 0, cv.width, cv.height);
      ctx.globalAlpha = 1;
    }
    const groups = new Map(plan.outline.groups.map((g) => [g.id, g]));
    order.forEach((id, rank) => {
      const g = groups.get(id)!;
      const hue = 220 - (220 * rank) / Math.max(1, order.length - 1);
      ctx.strokeStyle = `hsl(${hue} 80% ${id === sel ? 35 : 50}%)`;
      ctx.lineWidth = id === sel ? 5 : 2.5;
      ctx.lineCap = "round";
      for (const s of g.strokes) {
        ctx.beginPath();
        for (let i = 0; i < s.p.length; i += 2) (i ? ctx.lineTo : ctx.moveTo).call(ctx, s.p[i] * k, s.p[i + 1] * k);
        ctx.stroke();
      }
      const [x, y] = [g.strokes[0].p[0] * k, g.strokes[0].p[1] * k];
      ctx.fillStyle = "#000";
      ctx.font = "bold 11px system-ui";
      ctx.fillText(String(rank + 1), x + 3, y - 3);
    });
  }, [plan, layer, order, sel]);

  if (!el?.image) return null;
  const move = (i: number, to: number) => {
    const next = [...order];
    const [x] = next.splice(i, 1);
    next.splice(Math.max(0, Math.min(next.length, to)), 0, x);
    setOrder(next);
  };
  const save = () => {
    const natural = plan?.outline.groups.map((g) => g.id) ?? [];
    const same = natural.every((id, i) => order[i] === id);
    useEditor.getState().edit((d) => void (d.elements.find((e) => e.id === elementId)!.image!.groupOrder = same ? null : order));
    close();
  };

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal strokes" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Stroke order: {el.name || "image"}</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <p className="muted small">The outline is drawn group by group, blue first → red last. Select a group to highlight it; move it earlier or later.</p>
        <div className="strokes-body">
          <canvas ref={canvas} />
          <ol className="group-list">
            {order.map((id, i) => {
              const g = plan!.outline.groups.find((x) => x.id === id)!;
              return (
                <li key={id} className={sel === id ? "sel" : ""} onClick={() => setSel(id)}>
                  <span className="rank">{i + 1}</span>
                  <span className="gname">{id} · {g.strokes.length} stroke{g.strokes.length === 1 ? "" : "s"}</span>
                  <button className="icon" title="First" onClick={(e) => { e.stopPropagation(); move(i, 0); }}><ChevronsUp size={14} /></button>
                  <button className="icon" title="Earlier" onClick={(e) => { e.stopPropagation(); move(i, i - 1); }}><ArrowUp size={14} /></button>
                  <button className="icon" title="Later" onClick={(e) => { e.stopPropagation(); move(i, i + 1); }}><ArrowDown size={14} /></button>
                </li>
              );
            })}
          </ol>
        </div>
        <div className="analysis-actions">
          <button onClick={() => plan && setOrder(plan.outline.groups.map((g) => g.id))}><RotateCcw size={14} /> Automatic order</button>
          <button className="primary" onClick={save}>Save order</button>
        </div>
      </div>
    </div>
  );
}
